import { Link, useLocation } from 'react-router-dom';
import { useAppStore } from '@/stores/app-store';
import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Search, Sun, Moon } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';
import { Outlet } from 'react-router-dom';
import { Badge } from '@/components/Badge';
import { AuthPanel } from '@/components/AuthPanel';
import { CommandPalette, OPEN_PALETTE_EVENT } from '@/components/CommandPalette';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { NAV_ITEMS } from '@/lib/nav';
import { useIsSuperAdmin } from '@/lib/auth';
import { ENVIRONMENT, ENVIRONMENT_LABEL } from '@/config';

const LOGO_LIGHT = 'https://s3.us-east-1.amazonaws.com/co.knocking.images/knocking_typography_black_no_bg.png';
const LOGO_DARK = 'https://s3.us-east-1.amazonaws.com/co.knocking.images/knocking_typography_white_no_bg.png';

function BrandMark({ className }: { className?: string }) {
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <img src={LOGO_LIGHT} alt="Knocking" className="h-5 w-auto dark:hidden" />
      <img src={LOGO_DARK} alt="Knocking" className="h-5 w-auto hidden dark:block" />
      <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">Mission Control Admin</span>
    </span>
  );
}

export function Layout() {
  const location = useLocation();
  const { darkMode, toggleDarkMode, token, sessionExpired } = useAppStore();
  const isSuperAdmin = useIsSuperAdmin();
  const navItems = NAV_ITEMS.filter((i) => !i.superAdminOnly || isSuperAdmin);

  // Key on the top-level page segment only: navigating between a list and its
  // detail route (e.g. /tenants -> /tenants/:id) must NOT remount the page,
  // or local state like search and filters is wiped.
  const pageKey = '/' + (location.pathname.split('/')[1] ?? '');

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    if (sessionExpired) {
      toast.error('Session expired — please sign in again');
    }
  }, [sessionExpired]);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
            boxShadow: '0 8px 24px -8px rgb(0 0 0 / 0.18)',
            fontSize: '13px',
          },
        }}
      />
      {token && <CommandPalette />}

      {/* Sidebar — app chrome (page names, data vocabulary) is only shown
          after authentication; the public surface is just the sign-in card. */}
      {token && (
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden md:flex md:flex-col md:sticky md:top-0 md:h-screen">
        <div className="h-14 flex items-center px-4 border-b border-border">
          <BrandMark />
        </div>

        <div className="px-2 pt-3">
          <button
            onClick={() => window.dispatchEvent(new Event(OPEN_PALETTE_EVENT))}
            className="flex w-full items-center justify-between rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <span className="flex items-center gap-2">
              <Search className="h-3.5 w-3.5" />
              Jump to…
            </span>
            <kbd className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium">⌘K</kbd>
          </button>
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'relative flex items-center px-3 py-2 text-[13px] font-medium rounded-md transition-colors',
                  active
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-full bg-primary" />}
                <Icon className="h-4 w-4 mr-2.5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Environment</span>
            <Badge variant={ENVIRONMENT === 'production' ? 'destructive' : 'secondary'}>
              {ENVIRONMENT_LABEL}
            </Badge>
          </div>

          <button
            onClick={toggleDarkMode}
            className="flex items-center text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {darkMode ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
            {darkMode ? 'Light mode' : 'Dark mode'}
          </button>

          {token && (
            <div className="pt-3 border-t border-border">
              <AuthPanel />
            </div>
          )}
        </div>
      </aside>
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {token && (
        <>
        {/* Mobile header */}
        <header className="h-14 border-b border-border bg-card flex items-center px-4 md:hidden justify-between sticky top-0 z-20">
          <BrandMark />
          <Badge variant={ENVIRONMENT === 'production' ? 'destructive' : 'secondary'}>
            {ENVIRONMENT_LABEL}
          </Badge>
        </header>

        {/* Mobile nav */}
        <nav className="flex overflow-x-auto border-b border-border bg-card md:hidden px-2 py-1.5 gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors',
                  active
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                )}
              >
                <Icon className="h-3.5 w-3.5 mr-1.5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        </>
        )}

        <main className="flex-1 overflow-auto">
          {token ? (
            <div key={pageKey} className="mx-auto w-full max-w-6xl p-4 md:p-6 lg:p-8 animate-enter">
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </div>
          ) : (
            // Auth gate: pages never mount without a session, so no
            // unauthenticated requests fire and no raw 401s surface.
            <div className="min-h-full flex items-center justify-center p-4 animate-fade-in">
              <div className="w-full max-w-sm rounded-xl border bg-card p-8 space-y-5 shadow-pop animate-scale-in">
                <div className="space-y-3 text-center">
                  <img src={LOGO_LIGHT} alt="Knocking" className="h-[34px] w-auto mx-auto dark:hidden" />
                  <img src={LOGO_DARK} alt="Knocking" className="h-[34px] w-auto mx-auto hidden dark:block" />
                  <div className="space-y-1">
                    <h1 className="text-sm font-semibold tracking-tight">Mission Control Admin</h1>
                    <p className="text-xs text-muted-foreground">
                      Sign in with your Knocking account to manage Mission Control data. Authentication uses the{' '}
                      {ENVIRONMENT_LABEL} user pool.
                    </p>
                  </div>
                </div>
                <AuthPanel />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
